import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import InvoiceViewer from 'components/invoice/InvoiceViewer';
import InvoiceList from 'components/invoice/InvoiceList';
import useInput from 'lib/hooks/useInput';
import useDateInput from 'lib/hooks/useDateInput';
import PlainModal from 'components/common/modal/PlainModal';
import AppModal from 'components/common/modal/AppModal';
import CreateInvoiceModalContainer from 'containers/invoice/CreateInvoiceModalContainer';
import { AppActions, InvoiceActions } from 'store/actionCreators';
import { useShallowAppSelector } from 'store/hooks';
import { fetchInvoicePartners } from 'api/partner';
import { debounce } from 'lodash';
import { OffsetPagingData } from 'lib/types/sharedTypes';
import { useDidUpdateEffect } from 'lib/utils';
import T from 'components/common/text/T';
import { useTranslation } from 'react-i18next';
import {
  IinvoiceInformation,
  IindicationProjectUnit,
  InvoicePartner,
} from 'lib/types/invoiceTypes';
import PageTitle from 'components/base/title/PageTitle';
import { validInputString } from 'lib/library';

export default function InvoiceContainer() {
  const {
    invoicesData: { list: invoices, pagingData: invoicesPagingData },
    invoicesSuccess,
    invoicesFailure,
    invoiceData: { invoiceInformation, indicationProjectUnitList },
    invoiceDataSuccess,
    deleteInvoiceSuccess,
  } = useShallowAppSelector(state => ({
    invoicesData: state.invoice.invoices.data || {},
    invoicesSuccess: state.invoice.invoices.success,
    invoicesFailure: state.invoice.invoices.failure,
    invoiceData: state.invoice.invoice.data || {},
    invoiceDataSuccess: state.invoice.invoice.success,
    deleteInvoiceSuccess: state.invoice.deleteInvoice.success,
  }));
  const [isLoadSuccess, setIsLoadSuccess] = useState<boolean>(false);
  const [isLoadFailure, setIsLoadFailure] = useState<boolean>(false);
  // state
  const { t } = useTranslation();
  const page = useInput(1);
  const period = useDateInput(null);
  const partner = useInput('');
  const partnerKeyword = useInput('');
  const [fetchInvoicePartnersLoading, setFetchInvoicePartnersLoading] = useState(false);
  const [invoicePartners, setInvoicePartners] = useState<any[] | InvoicePartner[]>([]);
  const [invoiceInformationData, setInvoiceInformationData] = useState<IinvoiceInformation | any>(
    {},
  );
  const [indicationProjectUnitListData, setIndicationProjectUnitListData] = useState<
    IindicationProjectUnit[] | []
  >([]);
  const [invoicePartnersPagingData, setInvoicePartnersPagingData] =
    useState<null | OffsetPagingData>(null);
  const hasMoreInvoicePartners = invoicePartnersPagingData
    ? invoicePartnersPagingData.page < invoicePartnersPagingData.totalPage
    : false;
  const keyword = useInput('');
  const [createInvoiceModalOpen, setCreateInvoiceModalOpen] = useState(false);

  // SECTION: function
  const handleCloseCreateInvoiceModal = () => setCreateInvoiceModalOpen(false);
  const handleOpenCreateInvoiceModal = () => setCreateInvoiceModalOpen(true);
  const deleteInvoiceCode = useInput('');

  const [invoicesListCt, setInvoicesListCt] = useState<any[]>([]);

  let searchParams = useMemo(() => {
    const checkText = validInputString(keyword.value);
    keyword.setValue(checkText);
    return {
      page: page.value,
      partnerGroupIdx: partner.value,
      startDate: period.value ? period.value[0].unix() : null,
      endDate: period.value ? period.value[1].unix() : null,
      keyword: checkText || '',
    };
  }, [page.value, partner.value, period.value, keyword.value]);

  // SECTION: function
  const requestFetchInvoicePartners = async (value: {
    page?: number;
    keyword?: string;
    setLoading?: (value: boolean) => {};
  }) => {
    const { setLoading = () => {}, page = 1, keyword = '' } = value;
    setLoading(true);
    try {
      const { data } = await fetchInvoicePartners({ page, keyword });
      setLoading(false);
      return data;
    } catch (error) {
      AppActions.show_toast({ type: 'error', message: (error as { message: string })?.message });
      setLoading(false);
      return;
    }
  };

  const handleFetchInvoicePartners = debounce(async (first?: boolean) => {
    if (first) {
      const result = await requestFetchInvoicePartners({ keyword: '', page: 1 });
      setInvoicePartners(result?.list);
      setInvoicePartnersPagingData(result?.pagingData);
      return;
    }

    if (!hasMoreInvoicePartners) return;
    const result = await requestFetchInvoicePartners({
      keyword: partnerKeyword.value?.trim() || '',
      page: invoicePartnersPagingData ? invoicePartnersPagingData?.page + 1 : 1,
    });
    setInvoicePartners(draft => draft.concat(result?.list));
    setInvoicePartnersPagingData(result?.pagingData);
    return;
  }, 500);

  const handleSearchInvoicePartners = debounce(async (value: string) => {
    partnerKeyword.setValue(value);
    setInvoicePartners([]);
    const result = await requestFetchInvoicePartners({ keyword: value?.trim() || '', page: 1 });
    setInvoicePartners(result?.list);
    setInvoicePartnersPagingData(result?.pagingData);
  }, 500);

  const handleSearch = () => {
    page.setValue(1);
    InvoiceActions.fetch_invoices_request({ ...searchParams, page: 1 });
  };

  const handleChangePage = (value: number) => {
    page.setValue(value);
    InvoiceActions.fetch_invoices_request({ ...searchParams, page: value });
  };

  const [prevShowInvoiceId, setPrevShowInvoiceId] = useState('');
  const handleShowInvoiceView = (id: string) => {
    if (prevShowInvoiceId === id || !id) {
      setInvoiceInformationData({});
      setIndicationProjectUnitListData([]);
      setPrevShowInvoiceId('');
      return;
    } else {
      InvoiceActions.fetch_invoice_request({ id });
      setPrevShowInvoiceId(id);
    }
  };

  const handleDeleteInvoice = (id: string) => {
    InvoiceActions.delete_invoice_request({ invoiceCode: id });
    deleteInvoiceCode.setValue(id);
  };
  const handleRefreshList = () => {
    InvoiceActions.fetch_invoices_request(searchParams);
    setIsLoadSuccess(false);
    setIsLoadFailure(false);
  };
  // SECTION: DidMount
  useEffect(() => {
    handleFetchInvoicePartners(true);
    InvoiceActions.fetch_invoices_request(searchParams);
    return () => {
      setIsLoadSuccess(false);
      setIsLoadFailure(false);
    };
  }, []);

  // SECTION: DidUpdate
  useDidUpdateEffect(() => {
    handleSearch();
  }, [partner.value, period.value]);

  useDidUpdateEffect(() => {
    if (invoiceDataSuccess) {
      setInvoiceInformationData(invoiceInformation);
      setIndicationProjectUnitListData(indicationProjectUnitList);
    }
  }, [invoiceDataSuccess]);

  useDidUpdateEffect(() => {
    if (!invoicesSuccess) return;
    setInvoicesListCt(invoices);
    setIsLoadSuccess(true);
  }, [invoicesSuccess]);

  useDidUpdateEffect(() => {
    if (!invoicesFailure) return;
    setIsLoadFailure(true);
  }, [invoicesFailure]);

  useDidUpdateEffect(() => {
    if (deleteInvoiceSuccess) {
      InvoiceActions.fetch_invoices_request(searchParams);
    }
  }, [deleteInvoiceSuccess]);

  return (
    <>
      <PlainModal
        isOpen={createInvoiceModalOpen}
        onClose={handleCloseCreateInvoiceModal}
        width={1280}
      >
        <AppModal
          title={t('GLOBAL_NEW_INVOICE')}
          content={
            <CreateInvoiceModalContainer
              requestFetchInvoicePartners={requestFetchInvoicePartners}
              onClose={handleCloseCreateInvoiceModal}
            />
          }
          contentCardStyle={{
            borderRadius: '8px',
            marginBottom: '28px',
            padding: '38px 30px',
          }}
          isCloseIcon={true}
          onCancel={handleCloseCreateInvoiceModal}
          hideButton={true}
        />
      </PlainModal>
      <StyledInvoiceContainer>
        <PageTitle title={t('NAV_MENU_INVOICE')} />
        <div className="invoice__container">
          <div className="invoice__list_container">
            <InvoiceList
              // data
              invoices={invoicesListCt}
              invoicesPagingData={invoicesPagingData}
              invoicePartners={invoicePartners}
              hasMoreInvoicePartners={hasMoreInvoicePartners}
              onFetchInvoicePartners={handleFetchInvoicePartners}
              fetchInvoicePartnersLoading={fetchInvoicePartnersLoading}
              onSearchInvoicePartners={handleSearchInvoicePartners}
              // state
              isLoadSuccess={isLoadSuccess}
              isLoadFailure={isLoadFailure}
              page={page}
              period={period}
              partner={partner}
              keyword={keyword}
              onSearch={handleSearch}
              onChangePage={handleChangePage}
              onShowInvoiceView={handleShowInvoiceView}
              onOpenCreateInvoiceModal={handleOpenCreateInvoiceModal}
              onDeleteInvoice={handleDeleteInvoice}
              onRefreshList={handleRefreshList}
            />
          </div>
          <div className="invoice__pdf_container">
            <InvoiceViewer
              invoiceInformation={invoiceInformationData}
              indicationProjectUnitList={indicationProjectUnitListData}
            />
          </div>
        </div>
      </StyledInvoiceContainer>
    </>
  );
}

const StyledInvoiceContainer = styled.section`
  .invoice__container {
    display: flex;
    position: relative;
    margin-bottom: 100px;
    background-color: #eff1f8;
    border-radius: 15px;
    box-shadow: inset -3px -3px 6px rgba(0, 0, 0, 0.16);
    margin-top: 20px;

    &:before {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      box-shadow: inset 0 3px 6px rgba(0, 0, 0, 0.16);
      border-radius: 15px;
    }

    .invoice__list_container,
    .invoice__pdf_container {
      position: relative;
      width: 50%;
    }
    .invoice__list_container {
    }
    .invoice__pdf_container {
    }
  }
`;
